import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, Trash, UserPlus, Users } from "@phosphor-icons/react";
import {
  TierPricing,
  TierKey,
  ModulePricing,
  RealEstateKey,
  YearSlice,
  ManualCustomer,
  REAL_ESTATE_LABELS,
  EMPTY_UNITS,
  RealEstatePricingModelMap,
  CategoryMultipliers,
  customerTotalUnits,
  customerMRRDetailed,
  projectedClientMRR,
  projectionTotalUnits,
  tierModuleCostPerUnit,
  tierModuleCostPerM2,
} from "../types";

interface RevenueSimulatorProps {
  tierPricing: TierPricing;
  modulePricing: ModulePricing;
  rePricingModel: RealEstatePricingModelMap;
  categoryMultipliers: CategoryMultipliers;
  years: YearSlice[];
  onYearsChange: (years: YearSlice[]) => void;
}

const fmt = (n: number) => "\u20AC" + Math.round(n).toLocaleString("nl-BE");
const fmtN = (n: number) => Math.round(n).toLocaleString("nl-BE");

const SLIDER_MAX: Record<TierKey, number> = {
  free: 2000,
  starter: 300,
  growth: 150,
  pro: 60,
};

const reKeys = Object.keys(REAL_ESTATE_LABELS) as RealEstateKey[];

export const RevenueSimulator = ({
  tierPricing,
  modulePricing,
  rePricingModel,
  categoryMultipliers,
  years,
  onYearsChange,
}: RevenueSimulatorProps) => {
  const [activeYearIdx, setActiveYearIdx] = useState(0);
  const tiers = Object.keys(tierPricing) as TierKey[];
  const paidTiers = tiers.filter((t) => !tierPricing[t].isFree);

  const year = years[activeYearIdx];
  if (!year) return null;

  const updateYear = (patch: Partial<YearSlice>) => {
    const next = [...years];
    next[activeYearIdx] = { ...year, ...patch };
    onYearsChange(next);
  };

  // ── Manual customer CRUD ──
  const addManualCustomer = () => {
    const mc: ManualCustomer = {
      id: crypto.randomUUID(),
      name: `Klant ${year.manualCustomers.length + 1}`,
      tier: "starter",
      units: { ...EMPTY_UNITS },
      extraUsers: 0,
      oneOffPayment: 0,
    };
    updateYear({ manualCustomers: [...year.manualCustomers, mc] });
  };

  const updateCustomer = (id: string, patch: Partial<ManualCustomer>) => {
    updateYear({
      manualCustomers: year.manualCustomers.map((c) =>
        c.id === id ? { ...c, ...patch } : c
      ),
    });
  };

  const removeCustomer = (id: string) => {
    updateYear({
      manualCustomers: year.manualCustomers.filter((c) => c.id !== id),
    });
  };

  const updateCustomerUnits = (id: string, key: RealEstateKey, value: number) => {
    updateYear({
      manualCustomers: year.manualCustomers.map((c) =>
        c.id === id ? { ...c, units: { ...c.units, [key]: value } } : c
      ),
    });
  };

  // ── Projection changes ──
  const updateProjectionCount = (tier: TierKey, value: number) => {
    updateYear({
      projections: {
        ...year.projections,
        [tier]: { ...year.projections[tier], clientCount: value },
      },
    });
  };

  const updateProjectionAvg = (tier: TierKey, reKey: RealEstateKey, value: number) => {
    updateYear({
      projections: {
        ...year.projections,
        [tier]: {
          ...year.projections[tier],
          avgUnitsPerClient: { ...year.projections[tier].avgUnitsPerClient, [reKey]: value },
        },
      },
    });
  };

  // ── Add / remove year ──
  const addYear = () => {
    const lastYear = years[years.length - 1];
    const newYear: YearSlice = {
      year: lastYear.year + 1,
      manualCustomers: lastYear.manualCustomers.map((c) => ({
        ...c,
        id: crypto.randomUUID(),
      })),
      projections: JSON.parse(JSON.stringify(lastYear.projections)),
    };
    onYearsChange([...years, newYear]);
    setActiveYearIdx(years.length);
  };

  const removeYear = (idx: number) => {
    if (years.length <= 1) return;
    const next = years.filter((_, i) => i !== idx);
    onYearsChange(next);
    if (activeYearIdx >= next.length) setActiveYearIdx(next.length - 1);
  };

  // ── Revenue calculations ──
  const revenueData = useMemo(() => {
    const manualByTier: Record<TierKey, { clients: number; units: number; mrr: number }> = {
      free: { clients: 0, units: 0, mrr: 0 },
      starter: { clients: 0, units: 0, mrr: 0 },
      growth: { clients: 0, units: 0, mrr: 0 },
      pro: { clients: 0, units: 0, mrr: 0 },
    };
    let oneOffTotal = 0;
    for (const c of year.manualCustomers) {
      if (!tierPricing[c.tier]) continue;
      const units = customerTotalUnits(c);
      const mrr = customerMRRDetailed(c, tierPricing[c.tier], modulePricing, rePricingModel, categoryMultipliers);
      if (!manualByTier[c.tier]) manualByTier[c.tier] = { clients: 0, units: 0, mrr: 0 };
      manualByTier[c.tier].clients += 1;
      manualByTier[c.tier].units += units;
      manualByTier[c.tier].mrr += mrr;
      oneOffTotal += c.oneOffPayment || 0;
    }

    const projByTier: Record<TierKey, { clients: number; units: number; mrr: number }> = {
      free: { clients: 0, units: 0, mrr: 0 },
      starter: { clients: 0, units: 0, mrr: 0 },
      growth: { clients: 0, units: 0, mrr: 0 },
      pro: { clients: 0, units: 0, mrr: 0 },
    };
    for (const tier of tiers) {
      const proj = year.projections?.[tier];
      if (!proj || !tierPricing[tier]) continue;
      const avg = proj.avgUnitsPerClient;
      const totalUnitsPerClient = projectionTotalUnits(avg);
      const mrr = projectedClientMRR(avg, tierPricing[tier], modulePricing, rePricingModel);
      if (!projByTier[tier]) projByTier[tier] = { clients: 0, units: 0, mrr: 0 };
      projByTier[tier].clients = proj.clientCount;
      projByTier[tier].units = proj.clientCount * totalUnitsPerClient;
      projByTier[tier].mrr = mrr * proj.clientCount;
    }

    const manualTotalMRR = Object.values(manualByTier).reduce((s, v) => s + v.mrr, 0);
    const projTotalMRR = Object.values(projByTier).reduce((s, v) => s + v.mrr, 0);
    const manualClients = Object.values(manualByTier).reduce((s, v) => s + v.clients, 0);
    const projClients = Object.values(projByTier).reduce((s, v) => s + v.clients, 0);
    const manualUnits = Object.values(manualByTier).reduce((s, v) => s + v.units, 0);
    const projUnits = Object.values(projByTier).reduce((s, v) => s + v.units, 0);

    return {
      manualByTier,
      projByTier,
      manualTotalMRR,
      projTotalMRR,
      combinedMRR: manualTotalMRR + projTotalMRR,
      manualClients,
      projClients,
      manualUnits,
      projUnits,
      oneOffTotal,
      annualRevenue: (manualTotalMRR + projTotalMRR) * 12 + oneOffTotal,
    };
  }, [year, tierPricing, modulePricing, tiers]);

  // ── Year-over-year ──
  const yearSummaries = useMemo(() => {
    return years.map((y) => {
      let manualMRR = 0;
      let projMRR = 0;
      let oneOff = 0;
      for (const c of y.manualCustomers) {
        if (!tierPricing[c.tier]) continue;
        manualMRR += customerMRRDetailed(c, tierPricing[c.tier], modulePricing, rePricingModel, categoryMultipliers);
        oneOff += c.oneOffPayment || 0;
      }
      for (const tier of tiers) {
        const proj = y.projections?.[tier];
        if (!proj?.avgUnitsPerClient || !tierPricing[tier]) continue;
        projMRR += projectedClientMRR(proj.avgUnitsPerClient, tierPricing[tier], modulePricing, rePricingModel, categoryMultipliers) * proj.clientCount;
      }
      return { year: y.year, manualMRR, projMRR, totalMRR: manualMRR + projMRR, oneOff };
    });
  }, [years, tierPricing, modulePricing, tiers]);

  // Expand/collapse for projection RE breakdown
  const [expandedTier, setExpandedTier] = useState<TierKey | null>(null);

  return (
    <div className="space-y-4">
      {/* Year tabs */}
      <div className="flex items-center gap-2 flex-wrap">
        {years.map((y, idx) => (
          <button
            key={y.year}
            onClick={() => setActiveYearIdx(idx)}
            className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${
              idx === activeYearIdx
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-card border-border text-foreground hover:border-primary/40"
            }`}
          >
            {y.year}
            {years.length > 1 && idx === activeYearIdx && (
              <span
                className="ml-1.5 text-[10px] opacity-60 hover:opacity-100"
                onClick={(e) => { e.stopPropagation(); removeYear(idx); }}
              >
                &times;
              </span>
            )}
          </button>
        ))}
        <Button variant="outline" size="sm" onClick={addYear} className="h-7 gap-1 text-xs">
          <Plus size={12} /> Jaar toevoegen
        </Button>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="border-primary/40">
          <CardContent className="p-3">
            <p className="text-[10px] text-neutral-secondary-mid uppercase tracking-wider">Totale MRR</p>
            <p className="text-xl font-bold text-primary tracking-tight">{fmt(revenueData.combinedMRR)}</p>
            <p className="text-[10px] text-neutral-secondary-mid">
              {fmt(revenueData.annualRevenue)} / jaar
              {revenueData.oneOffTotal > 0 && <span className="text-amber-600"> (incl. {fmt(revenueData.oneOffTotal)} eenmalig)</span>}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3">
            <p className="text-[10px] text-neutral-secondary-mid uppercase tracking-wider">Actuele klanten</p>
            <p className="text-xl font-bold tracking-tight">
              <span className="text-emerald-600">{fmtN(revenueData.manualClients)}</span>
              <span className="text-neutral-secondary-mid text-sm font-normal mx-1">+</span>
              <span className="text-blue-600 text-base">{fmtN(revenueData.projClients)}</span>
              <span className="text-neutral-secondary-mid text-[10px] font-normal ml-1">proj.</span>
            </p>
            <div className="flex gap-2 mt-0.5">
              <span className="text-[10px] text-emerald-600">{fmt(revenueData.manualTotalMRR)} actueel</span>
              <span className="text-[10px] text-blue-600">{fmt(revenueData.projTotalMRR)} prognose</span>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3">
            <p className="text-[10px] text-neutral-secondary-mid uppercase tracking-wider">Eenheden in beheer</p>
            <p className="text-xl font-bold tracking-tight">{fmtN(revenueData.manualUnits + revenueData.projUnits)}</p>
            <p className="text-[10px] text-neutral-secondary-mid">{fmtN(revenueData.manualUnits)} actueel + {fmtN(revenueData.projUnits)} prognose</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3">
            <p className="text-[10px] text-neutral-secondary-mid uppercase tracking-wider">Gem. omzet / eenheid</p>
            <p className="text-xl font-bold tracking-tight">
              {(revenueData.manualUnits + revenueData.projUnits) > 0
                ? fmt(revenueData.combinedMRR / (revenueData.manualUnits + revenueData.projUnits))
                : "\u20AC0"}
            </p>
            <p className="text-[10px] text-neutral-secondary-mid">per eenheid /mnd</p>
          </CardContent>
        </Card>
      </div>

      {/* Year-over-year */}
      {years.length > 1 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-xs">Evolutie per jaar</CardTitle></CardHeader>
          <CardContent className="pt-0">
            <div className="flex gap-2 overflow-x-auto pb-1">
              {yearSummaries.map((ys, idx) => (
                <div key={ys.year} className="min-w-[140px] rounded-lg border p-3 flex-shrink-0">
                  <p className="text-xs font-bold mb-1">{ys.year}</p>
                  <div className="h-12 flex items-end gap-0.5">
                    <div className="bg-emerald-500 rounded-t w-8" style={{ height: `${Math.max(2, (ys.manualMRR / Math.max(...yearSummaries.map((s) => s.totalMRR), 1)) * 48)}px` }} />
                    <div className="bg-blue-400 rounded-t w-8" style={{ height: `${Math.max(2, (ys.projMRR / Math.max(...yearSummaries.map((s) => s.totalMRR), 1)) * 48)}px` }} />
                  </div>
                  <p className="text-xs font-semibold mt-1">{fmt(ys.totalMRR)}</p>
                  <p className="text-[9px] text-neutral-secondary-mid">
                    {fmt(ys.totalMRR * 12 + ys.oneOff)} ARR
                    {idx > 0 && yearSummaries[idx - 1].totalMRR > 0 && (
                      <span className="text-emerald-600 ml-1">
                        +{Math.round(((ys.totalMRR - yearSummaries[idx - 1].totalMRR) / yearSummaries[idx - 1].totalMRR) * 100)}%
                      </span>
                    )}
                  </p>
                </div>
              ))}
            </div>
            <div className="flex gap-4 mt-2">
              <span className="flex items-center gap-1 text-[10px]"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-500 inline-block" /> Actuele klanten</span>
              <span className="flex items-center gap-1 text-[10px]"><span className="w-2.5 h-2.5 rounded-sm bg-blue-400 inline-block" /> Prognose</span>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {/* Manual customers */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xs flex items-center gap-1.5">
                <UserPlus size={14} className="text-emerald-600" />
                Actuele klanten
                <span className="text-[10px] font-normal text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded">{year.manualCustomers.length}</span>
              </CardTitle>
              <Button variant="outline" size="sm" onClick={addManualCustomer} className="h-6 text-[10px] gap-1">
                <Plus size={10} /> Klant toevoegen
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            {year.manualCustomers.length === 0 ? (
              <p className="text-xs text-neutral-secondary-mid py-4 text-center">
                Voeg echte klanten toe met hun exacte eenheden per vastgoedtype
              </p>
            ) : (
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {year.manualCustomers.map((c) => {
                  const units = customerTotalUnits(c);
                  const mrr = customerMRRDetailed(c, tierPricing[c.tier], modulePricing, rePricingModel, categoryMultipliers);
                  return (
                    <div key={c.id} className="rounded-lg border p-3 space-y-2">
                      <div className="flex items-center gap-2">
                        <Input value={c.name} onChange={(e) => updateCustomer(c.id, { name: e.target.value })} className="h-7 text-xs font-medium flex-1" placeholder="Klantnaam..." />
                        <Select value={c.tier} onValueChange={(v) => updateCustomer(c.id, { tier: v as TierKey })}>
                          <SelectTrigger className="w-[100px] h-7 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent position="popper" sideOffset={4} align="end" className="max-h-[200px]">
                            {tiers.map((t) => (
                              <SelectItem key={t} value={t} className="text-xs">
                                {tierPricing[t].icon} {tierPricing[t].label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive hover:text-destructive" onClick={() => removeCustomer(c.id)}>
                          <Trash size={12} />
                        </Button>
                      </div>
                      {/* RE breakdown */}
                      <div className="grid grid-cols-4 gap-1">
                        {reKeys.map((k) => {
                          const info = REAL_ESTATE_LABELS[k];
                          return (
                            <div key={k} className="flex flex-col">
                              <span className="text-[9px] text-neutral-secondary-mid truncate">{info.icon} {info.label}</span>
                              <Input type="number" min="0" value={c.units[k] || ""} onChange={(e) => updateCustomerUnits(c.id, k, parseInt(e.target.value) || 0)} placeholder="0" className="h-6 text-[10px] text-right" />
                            </div>
                          );
                        })}
                      </div>

                      {/* Extra users, discount, one-off */}
                      <div className="grid grid-cols-3 gap-2">
                        <div className="flex flex-col">
                          <span className="text-[9px] text-neutral-secondary-mid">Extra gebruikers</span>
                          <Input type="number" min="0"
                            value={c.extraUsers || ""}
                            onChange={(e) => updateCustomer(c.id, { extraUsers: parseInt(e.target.value) || 0 })}
                            placeholder="0" className="h-7 text-xs text-right" />
                        </div>
                        <div className="flex flex-col">
                          <span className="text-[9px] text-neutral-secondary-mid">Extra korting %</span>
                          <Input type="number" min="0" max="100" step="1"
                            value={c.additionalDiscountPct || ""}
                            onChange={(e) => updateCustomer(c.id, { additionalDiscountPct: parseInt(e.target.value) || 0 })}
                            placeholder="0" className="h-7 text-xs text-right" />
                        </div>
                        <div className="flex flex-col">
                          <span className="text-[9px] text-amber-600">Eenmalig ({year.year})</span>
                          <div className="flex items-center gap-0.5">
                            <span className="text-xs text-neutral-secondary-mid">&euro;</span>
                            <Input type="number" min="0"
                              value={c.oneOffPayment || ""}
                              onChange={(e) => updateCustomer(c.id, { oneOffPayment: parseFloat(e.target.value) || 0 })}
                              placeholder="0" className="h-7 text-xs text-right" />
                          </div>
                        </div>
                      </div>

                      {/* Summary line */}
                      <div className="flex items-center justify-between text-[10px]">
                        <span className="text-neutral-secondary-mid">
                          {fmtN(units)} eenheden
                          {(c.additionalDiscountPct || 0) > 0 && <span className="text-blue-600 ml-1">(-{c.additionalDiscountPct}% korting)</span>}
                        </span>
                        <div className="text-right">
                          <span className="font-semibold text-emerald-700">{fmt(mrr)} /mnd</span>
                          {(c.oneOffPayment || 0) > 0 && (
                            <span className="text-amber-600 ml-1.5 text-[9px]">+ {fmt(c.oneOffPayment)} eenmalig</span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {year.manualCustomers.length > 0 && (
              <>
                <Separator className="my-2" />
                <div className="flex items-center justify-between text-xs">
                  <span className="font-medium text-emerald-700">Totaal actueel</span>
                  <div className="text-right">
                    <span className="font-bold text-emerald-700">{fmt(revenueData.manualTotalMRR)} /mnd</span>
                    {revenueData.oneOffTotal > 0 && (
                      <span className="block text-[10px] text-amber-600">+ {fmt(revenueData.oneOffTotal)} eenmalig ({year.year})</span>
                    )}
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Projections with RE breakdown */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs flex items-center gap-1.5">
              <Users size={14} className="text-blue-600" />
              Prognose klanten
              <span className="text-[10px] font-normal text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">extrapolatie</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 space-y-3">
            {tiers.map((tier) => {
              const info = tierPricing[tier];
              if (!info) return null;
              const proj = year.projections?.[tier];
              if (!proj) return null;
              const avg = proj.avgUnitsPerClient;
              const perClientMRR = projectedClientMRR(avg, tierPricing[tier], modulePricing, rePricingModel);
              const totalMRR = perClientMRR * proj.clientCount;
              const totalAvgUnits = projectionTotalUnits(avg);
              const isExpanded = expandedTier === tier;

              return (
                <div key={tier} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <button
                      className="text-xs font-semibold flex items-center gap-1.5 hover:text-primary transition-colors"
                      onClick={() => setExpandedTier(isExpanded ? null : tier)}
                    >
                      <span>{info.icon}</span>
                      {info.label}
                      <span className="text-[9px] text-neutral-secondary-mid font-normal">
                        ({fmtN(totalAvgUnits)} gem. units/klant)
                      </span>
                      <span className="text-[9px] text-neutral-secondary-mid">{isExpanded ? "\u25B2" : "\u25BC"}</span>
                    </button>
                    <span className="text-xs font-bold text-blue-600">{fmt(totalMRR)} /mnd</span>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] text-neutral-secondary-mid">Klanten</span>
                        <span className="text-[10px] font-medium tabular-nums">{fmtN(proj.clientCount)}</span>
                      </div>
                      <Slider
                        value={[proj.clientCount]}
                        min={0}
                        max={SLIDER_MAX[tier]}
                        step={tier === "free" ? 50 : tier === "starter" ? 5 : 1}
                        onValueChange={([v]) => updateProjectionCount(tier, v)}
                      />
                    </div>
                  </div>

                  {/* RE breakdown (expandable) */}
                  {isExpanded && (
                    <div className="rounded-md border bg-muted/30 p-2">
                      <p className="text-[9px] font-semibold text-neutral-secondary-mid uppercase tracking-wider mb-1.5">
                        Gemiddelde per klant per vastgoedtype
                      </p>
                      <div className="grid grid-cols-4 gap-1.5">
                        {reKeys.map((k) => {
                          const reInfo = REAL_ESTATE_LABELS[k];
                          const isM2 = rePricingModel[k] === "m2";
                          return (
                            <div key={k} className="flex flex-col">
                              <span className="text-[8px] text-neutral-secondary-mid truncate">
                                {reInfo.icon} {reInfo.label} <span className="opacity-60">({isM2 ? "m\u00B2" : "units"})</span>
                              </span>
                              <Input
                                type="number"
                                min="0"
                                value={avg[k] || ""}
                                onChange={(e) => updateProjectionAvg(tier, k, parseInt(e.target.value) || 0)}
                                placeholder="0"
                                className="h-6 text-[10px] text-right"
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div className="text-[9px] text-neutral-secondary-mid">
                    {fmt(tierPricing[tier].basePrice)} basis + {fmt(tierModuleCostPerUnit(tierPricing[tier], modulePricing))}/unit ({fmt(tierModuleCostPerM2(tierPricing[tier], modulePricing))}/m&sup2;) = {fmt(perClientMRR)}/klant &times; {fmtN(proj.clientCount)} = {fmt(totalMRR)}
                  </div>

                  {tier !== "pro" && <Separator />}
                </div>
              );
            })}
          </CardContent>
        </Card>
      </div>

      {/* Breakdown table */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-xs">Omzet breakdown &mdash; {year.year}</CardTitle></CardHeader>
        <CardContent className="pt-0">
          <div className="rounded-lg border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tier</TableHead>
                  <TableHead className="text-right"><span className="text-emerald-600">Actueel</span> klanten</TableHead>
                  <TableHead className="text-right"><span className="text-emerald-600">Actueel</span> MRR</TableHead>
                  <TableHead className="text-right"><span className="text-blue-600">Prognose</span> klanten</TableHead>
                  <TableHead className="text-right"><span className="text-blue-600">Prognose</span> MRR</TableHead>
                  <TableHead className="text-right">Totaal MRR</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paidTiers.map((tier) => {
                  if (!tierPricing[tier]) return null;
                  const m = revenueData.manualByTier[tier] || { clients: 0, mrr: 0 };
                  const p = revenueData.projByTier[tier] || { clients: 0, mrr: 0 };
                  return (
                    <TableRow key={tier}>
                      <TableCell className="font-medium text-xs">{tierPricing[tier].icon} {tierPricing[tier].label}</TableCell>
                      <TableCell className="text-right tabular-nums text-xs text-emerald-700">{m.clients}</TableCell>
                      <TableCell className="text-right tabular-nums text-xs text-emerald-700">{fmt(m.mrr)}</TableCell>
                      <TableCell className="text-right tabular-nums text-xs text-blue-600">{p.clients}</TableCell>
                      <TableCell className="text-right tabular-nums text-xs text-blue-600">{fmt(p.mrr)}</TableCell>
                      <TableCell className="text-right tabular-nums text-xs font-bold">{fmt(m.mrr + p.mrr)}</TableCell>
                    </TableRow>
                  );
                })}
                <TableRow className="border-t-2 font-bold">
                  <TableCell className="text-xs">Totaal</TableCell>
                  <TableCell className="text-right tabular-nums text-xs text-emerald-700">{revenueData.manualClients}</TableCell>
                  <TableCell className="text-right tabular-nums text-xs text-emerald-700">{fmt(revenueData.manualTotalMRR)}</TableCell>
                  <TableCell className="text-right tabular-nums text-xs text-blue-600">{revenueData.projClients}</TableCell>
                  <TableCell className="text-right tabular-nums text-xs text-blue-600">{fmt(revenueData.projTotalMRR)}</TableCell>
                  <TableCell className="text-right tabular-nums text-xs">{fmt(revenueData.combinedMRR)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
